/**
 * Sticky Cart Icon Component
 * ------------------------------------------------------------------------------
 * Shows a floating cart icon when the header scrolls out of view
 * Uses Intersection Observer for efficient scroll detection
 */

class StickyCartIcon extends HTMLElement {
  constructor() {
    super();

    this.header = null;
    this.stickyCart = null;
    this.observer = null;
    this._initialized = false;
  }

  connectedCallback() {
    if (!this._initialized) {
      this._initialized = true;
      this.initializeComponent();
    }
  }

  disconnectedCallback() {
    // Clean up observer when component is removed
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initializeComponent() {
    // Find the header element to observe
    this.header = document.querySelector('header');

    // Find the sticky cart icon
    this.stickyCart = document.getElementById('sticky-cart-icon');

    if (!this.header || !this.stickyCart) {
      console.warn('Sticky cart icon: Header or sticky cart element not found');
      return;
    }

    // Move to body to escape header's stacking context (z-index: 3)
    document.body.appendChild(this);

    this.setupIntersectionObserver();
    this.setupCartCountSync();

    // Initial sync of cart count and icon state
    this.updateCartCount();
    
    // Set initial accessibility state
    this.updateAccessibility();
  }

  /**
   * Set up Intersection Observer to watch header visibility
   */
  setupIntersectionObserver() {
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0, // Trigger when header is completely out of view
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // When header is NOT intersecting (out of view), show sticky cart
        if (!entry.isIntersecting) {
          this.stickyCart.classList.add('show');
        } else {
          // When header is visible, hide sticky cart
          this.stickyCart.classList.remove('show');
        }
        // Update accessibility attributes based on visibility
        this.updateAccessibility();
      });
    }, options);

    // Start observing the header
    this.observer.observe(this.header);
  }

  /**
   * Sync cart count updates between header cart and sticky cart
   */
  setupCartCountSync() {
    // Watch the header cart count element for changes
    const headerCartCount = document.querySelector(
      '#cart-icon-bubble .cart-count'
    );

    if (headerCartCount) {
      const countObserver = new MutationObserver(() => {
        this.updateCartCount();
      });

      countObserver.observe(headerCartCount, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  }

  /**
   * Update sticky cart count to match header cart count
   */
  updateCartCount() {
    const headerCount = document.querySelector('#cart-icon-bubble .cart-count');
    const stickyCount = this.stickyCart.querySelector('.cart-count');

    if (headerCount && stickyCount) {
      const countText = headerCount.textContent.trim();
      stickyCount.textContent = countText;

      // Add/remove 'has-items' class based on whether cart has items
      const hasItems = countText !== '' && parseInt(countText) > 0;

      if (hasItems) {
        this.stickyCart.classList.add('has-items');
      } else {
        this.stickyCart.classList.remove('has-items');
      }

      // Update accessibility attributes based on visibility
      this.updateAccessibility();
    }
  }

  /**
   * Update accessibility attributes based on visibility state
   * Only make focusable when both 'show' and 'has-items' classes are present
   */
  updateAccessibility() {
    const isVisible = this.stickyCart.classList.contains('show') && 
                      this.stickyCart.classList.contains('has-items');

    if (isVisible) {
      // Make focusable and visible to screen readers
      this.stickyCart.removeAttribute('aria-hidden');
      this.stickyCart.setAttribute('tabindex', '0');
    } else {
      // Hide from keyboard navigation and screen readers
      this.stickyCart.setAttribute('aria-hidden', 'true');
      this.stickyCart.setAttribute('tabindex', '-1');
    }
  }
}

customElements.define('sticky-cart-icon', StickyCartIcon);
